import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Play, Pause, RotateCcw } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const TOTAL = 60;

const PivotTimer = () => {
  const { user } = useAuth();
  const [seconds, setSeconds] = useState(TOTAL);
  const [running, setRunning] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(intervalRef.current);
          setRunning(false);
          onComplete();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line
  }, [running]);

  const onComplete = async () => {
    toast.success("60 seconds. You're still here.");
    if (user) {
      try {
        const { data } = await api.post("/pivot/log", { duration_seconds: TOTAL });
        toast.success(`Pivot streak: ${data.streak} day${data.streak === 1 ? "" : "s"}`);
      } catch {}
    }
  };

  const reset = () => {
    clearInterval(intervalRef.current);
    setRunning(false);
    setSeconds(TOTAL);
  };

  const progress = ((TOTAL - seconds) / TOTAL) * 100;
  const circumference = 2 * Math.PI * 70;
  const dashOffset = circumference - (progress / 100) * circumference;

  return (
    <div className="card-surface p-8" data-testid="pivot-timer">
      <div className="text-xs uppercase tracking-[0.2em] text-teal-300/80 mb-2">60 Seconds</div>
      <h3 className="font-display text-2xl text-neutral-100 mb-2">The number I was given back.</h3>
      <p className="text-xs text-neutral-500 mb-6 italic">
        Sixty seconds is how long my heart stopped. Now it's the length of a pause that can change your day.{" "}
        <a href="/story" className="text-teal-300/80 hover:text-teal-200 not-italic">Read the story →</a>
      </p>

      <div className="flex items-center gap-8">
        <div className="relative w-40 h-40 flex-shrink-0">
          <svg className="w-40 h-40 -rotate-90">
            <circle cx="80" cy="80" r="70" stroke="rgba(16,185,129,0.15)" strokeWidth="6" fill="none" />
            <circle
              cx="80" cy="80" r="70"
              stroke="#10b981"
              strokeWidth="6"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              style={{ transition: "stroke-dashoffset 1s linear" }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span data-testid="pivot-timer-value" className="font-display text-4xl text-neutral-100">{seconds}</span>
          </div>
        </div>

        <div className="flex-1">
          <p className="text-sm text-neutral-400 leading-relaxed mb-6">
            When the urge peaks, sit with it for exactly 60 seconds. Breathe. Watch it pass.
          </p>
          <div className="flex gap-3">
            {!running ? (
              <button
                onClick={() => setRunning(true)}
                data-testid="pivot-timer-start"
                className="btn-primary px-5 py-2 inline-flex items-center gap-2"
                disabled={seconds === 0}
              >
                <Play size={16} /> Start
              </button>
            ) : (
              <button
                onClick={() => setRunning(false)}
                data-testid="pivot-timer-pause"
                className="btn-ghost px-5 py-2 inline-flex items-center gap-2"
              >
                <Pause size={16} /> Pause
              </button>
            )}
            <button
              onClick={reset}
              data-testid="pivot-timer-reset"
              className="btn-ghost px-5 py-2 inline-flex items-center gap-2"
            >
              <RotateCcw size={16} /> Reset
            </button>
          </div>
          {!user && (
            <p className="text-xs text-neutral-500 mt-4">Sign up to log this to your streak.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PivotTimer;
